/* Two presentational components plus the padding rule that reserves their room,
   which callers apply to a clipping ancestor. */
/* eslint-disable react-refresh/only-export-components */
import {isClientFillable} from '../lib/client-fillable';
import {isRequiredCell} from '../lib/cell-fill';
import type {CellElement} from '../lib/table-types';
import {clientSlotLabelFor, useFillSurface} from './FillSurface';

/**
 * The markers drawn OUTSIDE a slot's box: whose it is, and whether it must be
 * filled.
 *
 * Outside, so neither sits on top of the control it marks — which is why the
 * room they overflow into has to be reserved by whatever clips them. That rule
 * is `markRoom`, and it lives here beside the offsets it compensates for.
 */

/**
 * Marks a slot as the CLIENT's to fill, not the crew's (DERP-247).
 *
 * A red asterisk says "someone must fill this"; on a client slot that is only
 * half the story, and the missing half is the one that matters on site — a crew
 * member hunting an unfillable field, or worse filling it in on the client's
 * behalf. So ownership is stated, not implied by the field being locked: a
 * disabled input with no explanation reads as a bug.
 *
 * `label` is required rather than defaulted: the word depends on who is
 * looking, and a default would quietly show a client the crew's word for their
 * own field on any path that forgot to pass it. `clientSlotLabelFor` answers it
 * from the surface's audience, so there is nothing to forget.
 */
function ClientMark({show, label}: {show?: boolean; label: string}) {
  if (!show) return null;
  return (
    <span className="pointer-events-none absolute -top-1.5 left-0 z-10 rounded-sm bg-sky-100 px-1 text-[7px] font-semibold tracking-wide text-sky-700 ring-1 ring-sky-300">
      {label}
    </span>
  );
}

/** The small red "*" overlaid on a required element's top-right corner. */
function RequiredMark({show}: {show?: boolean}) {
  if (!show) return null;
  return (
    <span className="absolute -top-1 -right-1 text-red-500 text-[10px] font-bold leading-none z-10 pointer-events-none">
      *
    </span>
  );
}

/**
 * Both out-of-box markers a slot can carry, drawn as one thing.
 *
 * They are never drawn apart — every renderer that shows one shows the other in
 * the same `relative` box, in this order — so they are one component rather than
 * a pair each caller reassembles. Five renderers hand-pairing them is five
 * chances to get the ordering, the z-index or the ownership test wrong, and the
 * test is easy to get wrong: `heading` carries no config at all, hence
 * `isClientFillable` rather than a plain read.
 *
 * Takes the slot's `config` rather than a computed boolean so a caller cannot
 * answer the ownership question differently from the API — see
 * `lib/client-fillable`.
 *
 * The ownership badge is dropped on an exported surface (DERP-237) — see
 * `FillSurface.forExport` for why that one is ours and `required` is the
 * document's. The room it occupies is still reserved, deliberately: the export
 * rasterises this very canvas, so a gutter that closed only on paper would move
 * every marked slot relative to what was signed off on screen.
 */
export function SlotMarks({required, config}: {required?: boolean; config: unknown}) {
  const {audience, forExport} = useFillSurface();
  return (
    <>
      <RequiredMark show={required} />
      <ClientMark
        show={isClientFillable(config) && !forExport}
        label={clientSlotLabelFor(audience)}
      />
    </>
  );
}

/**
 * Padding that reserves the room the marks overflow into.
 *
 * Both marks are positioned OUTSIDE the element's box so neither sits on top of
 * the control it marks. Any ancestor that clips overflow therefore cuts them off
 * — a table cell's wrapper does clip, so that a wide photo cannot make the page
 * scroll sideways — and a required cell looked optional.
 *
 * The values ARE the marks' own offsets, so they are stated once, here: a copy
 * at each clipping ancestor is a copy that can drift from the mark it
 * compensates for, and there are two ancestors. The client badge hangs further
 * out than the asterisk — 6px above against 4px, and on the left rather than the
 * right — so its presence sets the top gutter.
 *
 * Room is reserved ONLY for the marks actually present, so an unmarked element
 * keeps its exact previous geometry and no existing template shifts.
 */
export function markRoom({
  required,
  clientOwned,
}: {
  required: boolean;
  clientOwned: boolean;
}): string {
  if (clientOwned) return required ? 'pt-1.5 pr-1' : 'pt-1.5';
  return required ? REQUIRED_MARK_ROOM : '';
}

/** `markRoom` for a table cell element, which carries both answers itself.
 *  Shared by every cell view whose renderer draws its own marks, so they
 *  reserve the same room. */
export function cellMarkRoom(el: CellElement): string {
  return markRoom({required: isRequiredCell(el), clientOwned: isClientFillable(el.config)});
}

/** The room a lone `RequiredMark` needs. `markRoom`'s crew-side case, named
 *  because the fill renderers apply it directly to their clipping wrapper. */
export const REQUIRED_MARK_ROOM = 'pt-1 pr-1';

/**
 * A box that draws the out-of-box markers and reserves their room.
 *
 * For callers that render no mark of their own — the editor's static cell view.
 * The fill renderers each draw their own marks inside their own `relative` box,
 * so those take `markRoom` for the clipping wrapper instead.
 */
export function RequiredMarkFrame({
  show,
  config,
  children,
}: {
  show: boolean;
  config: unknown;
  children: React.ReactNode;
}) {
  const clientOwned = isClientFillable(config);
  if (!show && !clientOwned) return <>{children}</>;
  return (
    <div className={`relative ${markRoom({required: show, clientOwned})}`}>
      <SlotMarks required={show} config={config} />
      {children}
    </div>
  );
}
